import { call, put, select, takeLatest } from 'redux-saga/effects'

import { unleashClient } from '@context/FeatureToggle'
import { FeatureToggles } from '@enums'
import { IHateoasLink } from '@store'
import { fetchAgentSourcedCreators } from '@store/agentSourcedCreators/actions'
import { selectAuthToken } from '@store/auth/selectors'
import { fetchCampaignSuccess, selectCampaignLink } from '@store/campaigns'
import {
  startDiscoveryRun,
  startDiscoveryRunFailure,
  startDiscoveryRunSuccess,
} from '@store/discoveryRuns/actions'
import {
  ATTACH_NOTES_REL,
  buildNotesFormData,
  buildStartDiscoveryRunBody,
  buildStartDiscoveryRunFormData,
  extractVerbatimErrorMessage,
  isAlreadyAttachedError,
  isNotesLockedError,
  multipartRequest,
  START_DISCOVERY_RUN_REL,
} from '@store/discoveryRuns/api'
import { toast } from '@tribegroup'
import { networkRequest } from '@utils/networkRequest'

// Single orchestration saga: when the form has pending free-text notes,
// they are attached FIRST and awaited — a failure aborts the start — and
// only the START response's fetchCampaignSuccess is applied. Dispatching
// attachNotes + startDiscoveryRun concurrently risks out-of-order campaign
// snapshots briefly reverting the page to the zero-state form (inviting a
// double-start).
export function* startDiscoveryRunSaga(action: ReturnType<typeof startDiscoveryRun>) {
  const {
    campaignId,
    channels,
    notes,
    generateCriteria,
    transcriptFile,
    note,
    noteFile,
    similarHandles,
  } = action.payload
  try {
    const token: string = yield select(selectAuthToken)
    // start_discovery_run is a rel on the BRIEF's own top-level `links`.
    const link: IHateoasLink | undefined = yield select(
      selectCampaignLink,
      campaignId,
      START_DISCOVERY_RUN_REL,
    )
    if (!link) {
      // Surface the dead-end to the user, not just redux state (review fix).
      yield put(startDiscoveryRunFailure("Discovery can't be started right now."))
      yield call(toast, "Discovery can't be started right now.")
      return
    }

    // BRAND-9663 (BRAND-9605 tier 2): "Generate Criteria" on a rerun, or run
    // 1 with the toggle on. One multipart request carries channels +
    // generate_criteria + the fresh transcript/note together — deliberately
    // bypassing the attach-then-start flow below (see
    // IStartDiscoveryRunPayload's doc for why interleaving an attach here
    // would be unsafe).
    //
    // Checked via `!== undefined`/truthiness on EVERY field the atomic
    // bundle can carry, not just generateCriteria/transcriptFile: an
    // unchecked rerun explicitly sends generateCriteria: false (falsy, but
    // still a deliberate signal — `if (generateCriteria || ...)` alone
    // would miss it and fall through to the legacy dispatch below, which
    // has no generate_criteria field at all and lets Platform's rerun
    // default silently regenerate criteria against the user's intent).
    // noteFile/note are checked for the mirror reason: a run-1 submission
    // carrying only a notes file (transcript already attached from an
    // earlier session, nothing regenerating) has neither generateCriteria
    // nor transcriptFile set, and would otherwise silently drop the note
    // file by falling through to the legacy dispatch instead.
    //
    // Never entered on a resent transcript TEXT, which Platform's
    // start_discovery_run rejects as an invalid file type when
    // generate_criteria is true — transcriptFile/note/noteFile are sent
    // only when the user actually replaced/typed something fresh; the
    // brief's existing transcript/note is otherwise left completely
    // untouched server-side (see buildStartDiscoveryRunFormData's doc).
    if (generateCriteria !== undefined || transcriptFile || noteFile || note !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = yield call(
        multipartRequest,
        link.method,
        link.href,
        buildStartDiscoveryRunFormData(
          channels,
          transcriptFile,
          note,
          noteFile,
          generateCriteria,
          similarHandles,
        ),
        token,
      )
      yield put(fetchCampaignSuccess(response))
      yield put(startDiscoveryRunSuccess({ campaignId }))
      yield put(fetchAgentSourcedCreators({ campaignId }))
      yield call(toast, 'Discovery started · You can leave this page')
      return
    }

    // BRAND-9664: behind generateRerunCriteriaEnabled (BRAND-9659) — the
    // run-1 half of the same BRAND-9605 tier-2 rollout BRAND-9663 covers for
    // reruns — a pending note rides on the start_discovery_run body itself,
    // no separate attach_notes PUT.
    const sendNoteInline =
      Boolean(notes) && unleashClient.isEnabled(FeatureToggles.GENERATE_RERUN_CRITERIA_ENABLED)

    if (notes && !sendNoteInline) {
      // Attach pending free-text notes FIRST and await the outcome — its
      // full-brief-view response is deliberately NOT applied (only the start
      // response's snapshot is, below). A thrown error here lands in the
      // shared catch: the start never fires.
      const notesLink: IHateoasLink | undefined = yield select(
        selectCampaignLink,
        campaignId,
        ATTACH_NOTES_REL,
      )
      if (!notesLink) {
        // Notes are locked once a run has started/completed (BRAND-9604) — this
        // is a genuine, permanent "can't attach" state, not a transient failure.
        // Drop the unsendable text and continue the start rather than dead-ending
        // the whole rerun over stale client state (review fix).
        yield call(
          toast,
          "This brief's notes are locked — your notes weren't saved. Starting the run anyway.",
        )
      } else {
        try {
          yield call(
            multipartRequest,
            notesLink.method,
            notesLink.href,
            buildNotesFormData(undefined, notes),
            token,
          )
        } catch (notesError) {
          // note_locked_by_run reaches here when a cached snapshot still
          // carried attach_notes but the brief's run state moved on before
          // the click - same "drop and continue" treatment as the missing-rel
          // branch above, just discovered via a 422 instead of an absent
          // link (review fix). Notes already attached server-side (BE ahead
          // of the FE) also proceeds. Any other failure aborts the start
          // (rethrown into the shared catch below).
          if (isNotesLockedError(notesError)) {
            yield call(
              toast,
              "This brief's notes are locked — your notes weren't saved. Starting the run anyway.",
            )
          } else if (!isAlreadyAttachedError(notesError)) {
            throw notesError
          }
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = yield call(
      networkRequest,
      link.method,
      link.href,
      sendNoteInline
        ? buildStartDiscoveryRunBody(channels, notes, similarHandles)
        : buildStartDiscoveryRunBody(channels, undefined, similarHandles),
      token,
    )
    yield put(fetchCampaignSuccess(response))
    yield put(startDiscoveryRunSuccess({ campaignId }))
    yield put(fetchAgentSourcedCreators({ campaignId }))
    yield call(toast, 'Discovery started · You can leave this page')
  } catch (e: any) {
    const message = extractVerbatimErrorMessage(e)
    yield put(startDiscoveryRunFailure(message))
    yield call(toast, message)
  }
}

export default function* watchStartDiscoveryRun() {
  yield takeLatest(startDiscoveryRun.type, startDiscoveryRunSaga)
}
